import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get, post } from "../../../components/Utils/CallApi";
import { setPageLoadingTrue, setPageLoadingFalse } from "../appSlice";
import { setError } from "../appSlice";

export const fetchSectionTypes = createAsyncThunk(
  "podcasts/fetchSectionTypes",
  async ({ companyId }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = `companyId=${companyId}`;
      const response = await get("sections/type/list", params);
      return response.result;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

// Fetch podcasts list
export const fetchPodcasts = createAsyncThunk(
  "podcasts/fetchPodcasts",
  async (
    { companyId, page, limit, categoryId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(setPageLoadingTrue());

      let params = `companyId=${companyId}&sectionType=videos&page=${page}&limit=${limit}`;

      if (categoryId) {
        params += `&categoryId=${categoryId}`;
      }

      const response = await get("sections/data/list", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.result || [];
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const getVideo = createAsyncThunk(
  "videos/getVideo",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = `id=${id}&sectionType=videos`;
      const response = await get("sections/data/get", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.result || [];
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const savePodcast = createAsyncThunk(
  "podcasts/savePodcast",
  async (
    { payload, sectionId, files },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();
      const response = await post(
        "sections/data/save",
        payload,
        state.app,
        sectionId,
        files
      );

      return response.data;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

// Delete a podcast
export const deletePodcast = createAsyncThunk(
  "podcasts/deletePodcast",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const response = await post("sections/data/delete", { id });

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return id;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

// Order podcasts
export const orderPodcasts = createAsyncThunk(
  "podcasts/orderPodcasts",
  async (reorderedData, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());

      const token = localStorage.getItem("loginToken");

      const response = await post("sections/data/sort", {
        Authorization: token,
        reorderedData: reorderedData,
      });

      if (response.status === "failure") {
        return rejectWithValue(response.result);
      }
      return response.data;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

const podcastsSlice = createSlice({
  name: "podcasts",
  initialState: {
    podcastsContent: [],
    sectionTypes: [], // If needed
    loading: false,
    error: null,
  },
  reducers: {
    addPodcast: (state, action) => {
      if (action.payload) {
        state.podcastsContent.push(action.payload);
      }
    },
    editPodcast: (state, action) => {
      const index = state.podcastsContent.findIndex(
        (podcast) => podcast.id === action.payload.id
      );
      if (index !== -1) {
        state.podcastsContent[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch podcasts
      .addCase(fetchPodcasts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPodcasts.fulfilled, (state, action) => {
        state.loading = false;
        state.podcastsContent = action.payload;
      })
      .addCase(fetchPodcasts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save podcasts
      .addCase(savePodcast.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(savePodcast.fulfilled, (state, action) => {
        state.loading = false;
        state.podcastsContent = action.payload;
      })
      .addCase(savePodcast.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch section types
      .addCase(fetchSectionTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSectionTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.sectionTypes = action.payload;
      })
      .addCase(fetchSectionTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete podcast
      .addCase(deletePodcast.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePodcast.fulfilled, (state, action) => {
        state.loading = false;
        state.podcastsContent = state.podcastsContent.filter(
          (podcast) => podcast.id !== action.payload
        );
      })
      .addCase(deletePodcast.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Order podcasts
      .addCase(orderPodcasts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(orderPodcasts.fulfilled, (state, action) => {
        state.loading = false;

        const response = action.payload;

        if (response?.status === "success") {
          const reorderedData = action.meta.arg;
          state.podcastsContent = reorderedData.map((item) => {
            const existingItem = state.podcastsContent.find(
              (podcast) => podcast.id === item.id
            );
            return { ...existingItem, sortIndex: item.sortIndex };
          });
        } else {
          console.error("Unexpected API response:", response);
        }
      })
      .addCase(orderPodcasts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { addPodcast, editPodcast } = podcastsSlice.actions;
export default podcastsSlice.reducer;
